import { makeAutoObservable, runInAction } from 'mobx';
import React from 'react';
import { Client } from 'react-stomp-hooks';

import { axiosClient } from '../config/axios-client';
import { ChatMessage } from '../model/chat/ChatMessage';
import { ChatMessageRequest } from '../model/chat/ChatMessageRequest';
import { TargetEmojiMessage } from '../model/chat/TargetEmojiMessage';
import { Player } from '../model/Player';
import { isUnicodeEmoji } from '../utils/text.utils';
import sessionStorageStore from './SessionStorageStore';
import { StateStore } from './StateStore';
import tableStore from './TableStore';
import userSettingsStore from './UserSettingsStore';
import websocketStore from './WebsocketStore';

const BASE_CHAT_URL = '/chats';
export const MESSAGE_GLOWING_TIME = 5000;

class ChatStore implements StateStore {
  private chatMessages: Map<string, ChatMessage[]> = new Map();
  public targetMessages: Map<string, TargetEmojiMessage[]> = new Map();
  public lastChatsMessages: ChatMessage[] = [];

  public toggleChatEventMap: Map<string, React.MouseEvent<HTMLSpanElement>> =
    new Map();
  public selectedBots: Map<string, Player> = new Map();

  public chatStateChanged = 0;
  public targetEmojiStateChanged = 0;

  public scrollChatEnd: Map<string, boolean> = new Map();

  public constructor() {
    makeAutoObservable(this);
  }

  public resetState = (): void => {
    this.chatMessages = new Map();
    this.targetMessages = new Map();
    this.toggleChatEventMap = new Map();
    this.chatStateChanged = 0;
    this.targetEmojiStateChanged = 0;
  };

  public getTargetEmojiMessages = (tableId: string): TargetEmojiMessage[] =>
    this.targetMessages.get(tableId) || [];

  public addTargetEmojiMessage = (targetEmoji: TargetEmojiMessage): void => {
    this.targetMessages.set(targetEmoji.tableId, [
      ...this.getTargetEmojiMessages(targetEmoji.tableId),
      targetEmoji,
    ]);
    this.changeTargetEmojiState();
  };

  public deleteTargetEmojiMessages = (
    tableId: string,
    messageIds: string[],
  ): void => {
    this.targetMessages.set(
      tableId,
      this.getTargetEmojiMessages(tableId).filter(
        message => !messageIds.includes(message.id),
      ),
    );
    this.changeTargetEmojiState();
  };

  public setTableChatMessages = (
    tableId: string,
    chatMessages: ChatMessage[],
  ): void => {
    this.chatMessages.set(tableId, chatMessages);
    this.changeChatState();
  };

  public setActiveChatMessages = (chatMessages: ChatMessage[]): void => {
    chatMessages.forEach(message => {
      if (this.chatMessages.has(message.tableId)) {
        this.chatMessages.get(message.tableId).push(message);
      } else {
        this.chatMessages.set(message.tableId, [message]);
      }
    });
    this.changeChatState();
  };

  public getLastChatsMessages = (): ChatMessage[] => this.lastChatsMessages;

  public initChatMessages = (tableId: string): Promise<void> =>
    axiosClient
      .get<ChatMessage[]>(`${BASE_CHAT_URL}/${tableId}`)
      .then(response => response.data)
      .then(chatMessages => this.setTableChatMessages(tableId, chatMessages));

  public initActiveChatMessages = (): Promise<void> =>
    axiosClient
      .get<ChatMessage[]>(`${BASE_CHAT_URL}`)
      .then(response => response.data)
      .then(chatMessages => this.setActiveChatMessages(chatMessages));

  public initAndGetLastChatsMessages = (): Promise<ChatMessage[]> =>
    axiosClient
      .get<ChatMessage[]>(`${BASE_CHAT_URL}/last-messages`)
      .then(response => response.data)
      .then(response => {
        runInAction(() => {
          this.lastChatsMessages = response;
        });
        return response;
      });

  public sendMessage = (
    stompClient: Client,
    message: ChatMessageRequest,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `/tables/${message.tableId}/chat-messages`,
      message,
    );

  public addChatMessage = (chatMessage: ChatMessage): void => {
    if (!this.exists(chatMessage.tableId, chatMessage.id)) {
      this.chatMessages.set(chatMessage.tableId, [
        ...this.getChatMessages(chatMessage.tableId),
        chatMessage,
      ]);
      this.addLastChatMessage(chatMessage);
      this.changeChatState();
    }
  };

  public addLastChatMessage = (chatMessage: ChatMessage): void => {
    if (
      !chatMessage.emoji &&
      !isUnicodeEmoji(chatMessage.text) &&
      !chatMessage.myBot
    ) {
      this.lastChatsMessages.unshift(chatMessage);
    }
  };

  public openChat = (
    tableId: string,
    maxAttempts = 20,
    interval = 200,
  ): void => {
    let currentAttempt = 0;
    const tryToFindAndScroll = (): void => {
      const chat = document.querySelector<HTMLDivElement>(
        `#chat${
          userSettingsStore.settings.table.expertMode ? '-expert-mode' : ''
        }-${tableId}`,
      );
      const chatButton = document.querySelector<HTMLButtonElement>(
        `#chat${
          userSettingsStore.settings.table.expertMode ? '-expert-mode' : ''
        }-btn-${tableId}`,
      );
      if (!chat && chatButton) {
        chatButton.click();
      } else if (currentAttempt < maxAttempts) {
        currentAttempt++;
        setTimeout(tryToFindAndScroll, interval);
      }
    };
    tryToFindAndScroll();
  };

  public waitUntilChatIsClosed = (
    tableId: string,
    maxAttempts = 10,
    interval = 100,
  ): Promise<void> => {
    return new Promise(resolve => {
      let currentAttempt = 0;
      const tryToFind = (): void => {
        const chat = document.querySelector<HTMLDivElement>(`#chat-${tableId}`);
        if (!chat) {
          resolve();
        } else if (currentAttempt < maxAttempts) {
          currentAttempt++;
          setTimeout(tryToFind, interval);
        } else {
          resolve();
        }
      };
      tryToFind();
    });
  };

  public scrollToMessage = (
    messageId: string,
    maxAttempts = 20,
    interval = 200,
  ): void => {
    let currentAttempt = 0;
    const tryToFindAndScroll = (): void => {
      const message = document.querySelector<HTMLDivElement>(
        `#message-${messageId}`,
      );
      if (message) {
        message.scrollIntoView({ block: 'center' });
        this.glowMessage(message);
      } else if (currentAttempt < maxAttempts) {
        currentAttempt++;
        setTimeout(tryToFindAndScroll, interval);
      }
    };
    tryToFindAndScroll();
  };

  public glowMessage = (message: HTMLDivElement): void => {
    if (message) {
      const className = 'glowing-message';
      message.classList.add(className);

      setTimeout(() => {
        message.classList.remove(className);
      }, MESSAGE_GLOWING_TIME);
    }
  };

  public openAndScrollTableAndOpenChat = (
    tableId: string,
    messageId: string,
  ): void => {
    if (!tableStore.isTableViewing(tableId)) {
      tableStore.addViewingTable(tableId);
    }
    tableStore.scrollToTable(tableId);
    if (!userSettingsStore.settings.chat.disabled) {
      this.openChat(tableId);
      this.scrollToMessage(messageId);
    }
  };

  public openTableAndOpenChatExpertMode = (
    tableId: string,
    messageId: string,
  ): void => {
    if (!tableStore.isTableViewing(tableId)) {
      tableStore.addViewingTable(tableId);
    }
    if (!userSettingsStore.settings.chat.disabled) {
      this.openChat(tableId);
      this.scrollToMessage(messageId);
    }
  };

  public handleNotificationChatMessageClick = (
    chatMessage: ChatMessage,
  ): void => {
    this.handleNotificationChatMessageClickById(
      chatMessage.tableId,
      chatMessage.id,
    );
  };

  public handleNotificationChatMessageClickById = (
    tableId: string,
    messageId: string,
  ): void => {
    this.resetToggleChatEventMap();
    this.waitUntilChatIsClosed(tableId).then(() => {
      this.setScrollChatEnd(tableId, false);
      if (userSettingsStore.settings.table.expertMode) {
        this.openTableAndOpenChatExpertMode(tableId, messageId);
      } else {
        this.openAndScrollTableAndOpenChat(tableId, messageId);
      }
      setTimeout(
        () => this.setScrollChatEnd(tableId, true),
        MESSAGE_GLOWING_TIME,
      );
    });
  };

  public exists = (tableId: string, messageId: string): boolean =>
    this.getChatMessages(tableId).some(message => message.id === messageId);

  public getChatMessages = (tableId: string): ChatMessage[] =>
    this.chatMessages.get(tableId) || [];

  public getUnreadMessageNumber = (tableId: string): number => {
    const readChatMessages =
      sessionStorageStore.getReadChatMessagesIds(tableId);
    const unreadLength = this.getChatMessages(tableId).filter(
      m => !readChatMessages.includes(m.id),
    ).length;
    return unreadLength >= 0 ? unreadLength : 0;
  };

  public getUnreadTextMessagesCount = (tableId: string): number => {
    const readChatMessages =
      sessionStorageStore.getReadChatMessagesIds(tableId);
    return this.getChatMessages(tableId).filter(
      message =>
        !message.emoji &&
        !message.myBot &&
        !readChatMessages.includes(message.id),
    ).length;
  };

  public getUnreadEmojiMessagesCount = (tableId: string): number => {
    const readChatMessages =
      sessionStorageStore.getReadChatMessagesIds(tableId);
    return this.getChatMessages(tableId).filter(
      message =>
        message.emoji &&
        !message.myBot &&
        !readChatMessages.includes(message.id),
    ).length;
  };

  public markMessagesAsRead = (tableId: string): void => {
    sessionStorageStore.addReadChatMessagesIds(
      tableId,
      this.getChatMessages(tableId).map(message => message.id),
    );
    this.changeChatState();
  };

  public removeMessages = (tableId: string): void => {
    this.chatMessages.delete(tableId);
  };

  public changeChatState = (): void => {
    this.chatStateChanged++;
  };

  public changeTargetEmojiState = (): void => {
    this.targetEmojiStateChanged++;
  };

  public getAllChatMessages = (): Map<string, ChatMessage[]> =>
    this.chatMessages;

  public setChatMessages = (chatMessages: Map<string, ChatMessage[]>): void => {
    this.chatMessages = chatMessages;
    this.changeChatState();
  };

  public resetToggleChatEventMap = (): void => {
    this.toggleChatEventMap = new Map();
  };

  public addToggleChatEventMap = (
    tableId: string,
    event: React.MouseEvent<HTMLSpanElement>,
  ): void => {
    this.toggleChatEventMap.set(tableId, event);
  };

  public removeToggleEvent = (tableId: string): void => {
    this.toggleChatEventMap.delete(tableId);
  };

  public addSelectedBot = (tableId: string, player: Player): void => {
    this.selectedBots.set(tableId, player);
  };

  public getSelectedBot = (tableId: string): Player | undefined =>
    this.selectedBots.get(tableId);

  public setScrollChatEnd = (tableId: string, scrollChatEnd: boolean): void => {
    this.scrollChatEnd.set(tableId, scrollChatEnd);
  };

  public isScrollChatEnd = (tableId: string): boolean =>
    !this.scrollChatEnd.has(tableId) || this.scrollChatEnd.get(tableId);
}

const chatStore = new ChatStore();
export default chatStore;
